import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from './auth.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs';
import { UserRole } from '../models/user.model';
import { ToastService } from '../services/toast.service';

const KYC_SUBMISSION_GRACE_MS = 30 * 60 * 1000;
const LATEST_KYC_SUBMISSION_KEY = 'arris.latestKycSubmissionAt';

function getRoleHomeRoute(role: UserRole): string {
  if (role === 'Admin') {
    return '/admin';
  }

  if (role === 'Client') {
    return '/client';
  }

  if (role === 'Borrower') {
    return '/borrower';
  }

  return '/login';
}

function hasRecentKycSubmission(): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }

  const submittedAt = sessionStorage.getItem(LATEST_KYC_SUBMISSION_KEY);
  if (!submittedAt) {
    return false;
  }

  const submittedAtMs = new Date(submittedAt).getTime();
  if (Number.isNaN(submittedAtMs)) {
    return false;
  }

  const ageMs = Date.now() - submittedAtMs;
  return ageMs >= 0 && ageMs <= KYC_SUBMISSION_GRACE_MS;
}

/**
 * A flexible, role-based authentication guard that also handles KYC status redirects.
 * It waits for the initial authentication check to complete before making a decision.
 */
export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router: Router = inject(Router);
  const toastService = inject(ToastService);

  // This guard can be used with both canActivate (role on `route`) and canActivateChild (role on `route.parent`)
  const requiredRole = (route.data['requiredRole'] || route.parent?.data['requiredRole']) as UserRole;

  return toObservable(authService.isLoading).pipe(
    filter(isLoading => !isLoading), // Wait until the auth state is resolved
    take(1),
    map(() => {
      const userProfile = authService.userProfile();
      
      // 1. Check for authentication
      if (!userProfile) {
        return router.parseUrl('/login');
      }

      // 2. Check for correct role
      if (requiredRole && userProfile.role !== requiredRole) {
        const redirectUrl = getRoleHomeRoute(userProfile.role);
        return router.parseUrl(redirectUrl);
      }
      
      // 3. Handle KYC status for Client role
      if (userProfile.role === 'Client') {
        const accessState = authService.clientAccessState();
        const targetUrl = state.url;
        const isKycQuestionnairePage = targetUrl.startsWith('/client/kyc-questionnaire');
        const isFinancialQuizPage = targetUrl.startsWith('/client/financial-quiz');
        const isKycPendingPage = targetUrl.startsWith('/client/kyc-pending');
        const isKycPage =
          isKycQuestionnairePage ||
          isFinancialQuizPage ||
          isKycPendingPage;
        const allowKycEdit = route.queryParamMap.get('edit') === '1';
        const recentSubmission = hasRecentKycSubmission();

        if (accessState.hasAccess) {
          if (isKycPage) {
            return router.parseUrl('/client/dashboard');
          }

          return true;
        }

        if (isKycPage) {
          if (accessState.reason === 'KYC_PENDING') {
            if (isKycQuestionnairePage && !allowKycEdit) {
              return router.parseUrl('/client/kyc-pending');
            }

            return true;
          }

          if (accessState.reason === 'KYC_ACTION_REQUIRED') {
            if (recentSubmission && isKycPendingPage) {
              return true;
            }

            if (isFinancialQuizPage) {
              return true;
            }

            if (isKycQuestionnairePage) {
              return allowKycEdit || !recentSubmission ? true : router.parseUrl('/client/kyc-pending');
            }
          }

          if (accessState.reason === 'QUIZ_PENDING') {
            if (isFinancialQuizPage) {
              return router.parseUrl('/client/kyc-pending');
            }

            return true;
          }

          if (accessState.reason === 'QUIZ_ACTION_REQUIRED') {
            if (isFinancialQuizPage) {
              return true;
            }

            if (isKycQuestionnairePage) {
              return router.parseUrl('/client/financial-quiz');
            }
          }

          if (accessState.blockingRoute && !targetUrl.startsWith(accessState.blockingRoute)) {
            return router.parseUrl(accessState.blockingRoute);
          }

          return true;
        }

        const kycRequiredForRoute = route.data['kycRequired'] === true;

        if (kycRequiredForRoute) {
          toastService.showError(accessState.bannerMessage || 'Compliance approval is required before access is enabled.');
          if (accessState.reason === 'KYC_ACTION_REQUIRED' && recentSubmission) {
            return router.parseUrl('/client/kyc-pending');
          }

          return router.parseUrl(accessState.blockingRoute || '/client/kyc-questionnaire');
        }

        return true;
      }


      // 4. If all checks pass (i.e., user is an Admin), allow access
      return true;
    })
  );
};

/**
 * Prevents authenticated users from accessing the login page.
 * It waits for the initial authentication check to complete before making a decision.
 */
export const canActivateLogin: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router: Router = inject(Router);

  return toObservable(authService.isLoading).pipe(
    filter(isLoading => !isLoading), // Wait until the auth state is resolved
    take(1),
    map(() => {
      if (authService.isAuthenticated()) {
        // If already logged in, redirect to the appropriate dashboard
        const userProfile = authService.userProfile();
        const redirectUrl = userProfile?.role ? getRoleHomeRoute(userProfile.role) : '/login';
        return router.parseUrl(redirectUrl);
      }
      // Not logged in, allow access to the login page
      return true;
    })
  );
};
